<template>
  <app-widget :loading="loading" :title="$t('common.moves')">
    <v-btn
      slot="widget-header-action"
      class="ml-auto"
      color="success"
      :to="routeConstants.MOVES_CALENDAR_URL"
      data-cy="moves-calendar"
    >
      <v-icon left>
        date_range
      </v-icon>
      {{ $t('move.actions.calendar') }}
    </v-btn>
    <v-data-table
      slot="widget-content"
      :items="moves"
      :headers="headers"
      :options.sync="options"
      :server-items-length="movesCount"
      sort-by="startDate"
      :footer-props="{
        'items-per-page-options': [5, 10, 15]
      }"
      must-sort
      data-cy="moveTable"
    >
      <template v-slot:item.transfereeName="{ item }">
        <span>
          {{item.transfereeFirstName}} {{item.transfereeLastName}}
          <v-tooltip
            v-if="item.transfereeFirstName || item.transfereeLastName"
            top
          >
            <template v-slot:activator="{ on }">
              <v-btn x-small icon v-on="on">
                <v-icon small>
                  info
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('move.properties.phone') }}: {{item.transfereePhoneNumber}}</span>
          </v-tooltip>
        </span>
      </template>
      <template v-slot:item.startDate="{ item }">
        <span>{{new Date(item.startDate).toLocaleDateString()}}</span>
      </template>
      <template v-slot:item.plannedDeliveryDate="{ item }">
        <span>{{new Date(item.plannedDeliveryDate).toLocaleDateString()}}</span>
      </template>
      <template v-slot:item.origin="{ item }">
      <span>{{item.origin.street}} {{item.origin.number}}<br>
        {{item.origin.city}}{{item.origin.state ? ', ' : null}}{{item.origin.state}}{{item.origin.country ? ', ' : null}}{{item.origin.country}}</span>
      </template>
      <template v-slot:item.destination="{ item }">
      <span>{{item.destination.street}} {{item.destination.number}}<br>
        {{item.destination.city}}{{item.destination.state ? ', ' : null}}{{item.destination.state}}{{item.destination.country ? ', ' : null}}{{item.destination.country}}</span>
      </template>
      <template v-slot:item.moveStatus="{ item }">
        <span>{{getMoveStatus(item.moveStatus)}}</span>
      </template>
      <template v-slot:item.device.name="{ item }">
        <span>
          {{item.device ? item.device.name : null}}
          <v-tooltip v-if="item.device" top>
            <template v-slot:activator="{ on }">
              <v-btn x-small icon v-on="on">
                <v-icon small>
                  info
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('device.fields.imei') }}: {{item.device.imei}}</span>
          </v-tooltip>
        </span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-btn
              small
              v-on="on"
              color="transparent"
              data-cy="movesActionsBtn"
              :data-cy-assign-device="!item.device ? 'assign' : 'remove'"
            >
              <v-icon small>
                more_horiz
              </v-icon>
            </v-btn>
          </template>
          <v-list dense data-cy="movesActionsList">
            <v-list-item v-if="!item.device" @click="openDialog(item)">
              <v-list-item-icon class="mr-0">
                <v-icon small color="success">
                  mobile_friendly
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('move.actions.assignDevice') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              @click="showTrackingHistory(item)"
              v-if="item.device"
            >
              <v-list-item-icon class="mr-0">
                <v-icon small color="success">
                  track_changes
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('move.calendar.showTrackingHistory') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item @click="createShippingPDF(item)">
              <v-list-item-icon class="mr-0">
                <v-icon small color="success">
                  info
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('move.actions.shippingInfo') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="item.device && item.moveStatus === 0" @click="openDialog(item)">
              <v-list-item-icon class="mr-0">
                <v-icon small color="error">
                  delete
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('move.actions.removeDevice') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <assign-device-modal
      slot="widget-content"
      :showDialog="dialog"
      :selectedMove="selectedMove"
      @dialog="setDialog"
    />
  </app-widget>
</template>

<script>
  import * as actions from '@/store/actions.type';
  import AppWidget from '@/components/widgets/AppWidget';
  import AssignDeviceModal from '@/components/devices/AssignDeviceModal';
  import * as routeConstants from '@/router/constants';
  import {mapActions, mapGetters} from 'vuex';
  import * as moveEnums from '@/constants/move';
  import {
    MOVE_STATUS_TYPE_ARR,
  } from '@/constants';
  import jsPDF from 'jspdf';

  export default {
    name: 'MovesTable',
    data: () => ({
      headers: [],
      options: {},
      routeConstants: routeConstants,
      dialog: false,
      selectedMove: null,
    }),
    props: {
      loading: Boolean,
    },
    components: {
      AppWidget,
      AssignDeviceModal
    },
    computed: {
      ...mapGetters([
        'moves',
        'movesCount',
        'tokenData',
      ]),
    },
    watch: {
      options: {
        handler(options) {
          const sortBy = Object.keys(moveEnums.SORT_MOVES_BY_TYPE).
            find(key => moveEnums.SORT_MOVES_BY_TYPE[key] === options.sortBy[0]);

          const sortDirection = Object.keys(moveEnums.SORT_DESCENDING).
            find(key => moveEnums.SORT_DESCENDING[key] === options.sortDesc[0]);

          const skipMoves = (options.page - 1) * options.itemsPerPage;

          const payload = {
            sortMovesBy: sortBy,
            sortDirection: sortDirection,
            take: options.itemsPerPage,
            skip: skipMoves > 0 ? skipMoves : null,
          };

          if (options.sortBy.length || options.sortDesc.length || options.itemsPerPage || options.page) {
            this.fetchMovesAction(payload);
          }
        },
        deep: true,
      },
    },
    mounted() {
      this.headers = [
        {text: this.$t('move.properties.name'), value: 'name', sortable: true},
        {text: this.$t('move.properties.transferee'), value: 'transfereeName', sortable: true},
        {text: this.$t('move.properties.rmcAbbr'), value: 'relocationManagementCompany', sortable: true},
        {text: this.$t('move.properties.startDate'), value: 'startDate', sortable: true},
        {text: this.$t('move.properties.endDate'), value: 'plannedDeliveryDate', sortable: true},
        {text: this.$t('move.properties.origin'), value: 'origin', sortable: true},
        {text: this.$t('move.properties.destination'), value: 'destination', sortable: true},
        {text: this.$t('move.properties.status'), value: 'moveStatus', sortable: true},
        {text: this.$t('move.properties.tracker'), value: 'device.name', sortable: true},
        {text: this.$t('move.properties.actions'), value: 'actions', sortable: false},
      ];

      this.$root.$on('filterUpdate', () => {
        this.options.page = 1;
      });
    },
    destroyed() {
      this.resetMoveParamsAction();
    },
    methods: {
      ...mapActions({
        fetchMovesAction: actions.FETCH_MOVES,
        resetMoveParamsAction: actions.RESET_MOVE_PARAMS,
      }),
      getMoveStatus(key) {
        return this.$t(`move.type.${MOVE_STATUS_TYPE_ARR[key]}`);
      },
      createShippingPDF(item) {
        let doc = new jsPDF('l', 'mm', 'a5');

        doc.text(this.$t('move.shippingInfoPDF.heading', {moveName: item.name}), 10, 20);
        doc.text(this.$t('move.shippingInfoPDF.address', {
          street: item.deviceShippingAddress.street,
          number: item.deviceShippingAddress.number,
        }), 10, 40);
        doc.text(this.$t('move.shippingInfoPDF.address2ndLine', {
          country: item.deviceShippingAddress.country,
          state: item.deviceShippingAddress.state,
          city: item.deviceShippingAddress.city,
          zipCode: item.deviceShippingAddress.zipCode,
          separator: item.deviceShippingAddress.state ? ', ' : undefined,
          separator2: item.deviceShippingAddress.country ? ', ' : undefined,
        }), 57, 50);
        doc.text(this.$t('move.shippingInfoPDF.customerName', {
          customerFirstName: item.transfereeFirstName,
          customerLastName: item.transfereeLastName,
        }), 10, 60);
        doc.text(this.$t('move.shippingInfoPDF.phoneNumber', {phoneNumber: item.transfereePhoneNumber}), 10, 70);

        doc.setProperties({
          title: item.name,
          subject: this.$t('move.shippingInfoPDF.heading', {moveName: item.name}),
          author: this.tokenData.name,
        });

        this.$emit('openPDF', doc.output('dataurlstring'));
      },
      showTrackingHistory(item) {
        this.$router.push({
          path: routeConstants.DEVICE_DETAILS_URL.replace(':id', item.device.id),
          query: {moveId: item.moveId}
        })
      },
      setDialog(value) {
        this.dialog = value;
      },
      openDialog(item) {
        this.selectedMove = item;
        setTimeout(() => this.dialog = true);
      },
    },
  };
</script>
